import { Observable } from 'rxjs/Observable';
import { EventSource } from './eventSource';

import { nullSorter } from './sort';

export interface TravelItem {
    address: string;
    name: string;
    notes?: string;
    tel?: string;
    url?: string;
    sortKey?: number;
    dtstart: number;
    dtend: number;
    items: Array<{}>
}

export type CombinedTravel = TravelItem[] 

import { 
    Photo, 
    TravelList, 
    TravelSleep, 
    TravelSleepItem, 
    TravelTransportation, 
    TravelNote,
    TravelBooking
 } from './interfaces';

export class EventTravel {

    private es: EventSource;

    constructor(eventSource: EventSource) {
        this.es = eventSource;
    }

    static sortLists = (list, key) => {
        if (!list[key]) {
            return [];
        } else {
            return Object.keys(list[key]).map(item => {
                list[key][item].key = item;
                list[key][item].type = key;
                list[key][item].sortKey = parseInt(list[key][item].sortKey);
                return list[key][item];
            }).sort(nullSorter);
        }
    }
    public observeLists(): Observable<Array<TravelList>> {
        return this.es.pluckData(this.es.observeTravelPage(), ['lists'])
            .map(lists => Object.keys(lists).map((listKey, idx) => {
                lists[listKey].key = listKey;
                lists[listKey].sortKey = (lists[listKey].sortKey) ? parseInt(lists[listKey].sortKey) : idx;
                lists[listKey].type = 'list';
                lists[listKey].items = Object.keys(lists[listKey].items || {}).map(itemKey => {
                    lists[listKey].items[itemKey].key = itemKey;
                    return lists[listKey].items[itemKey];
                })
                return lists[listKey] 
            }).filter(x => x)).map(data => data.sort(nullSorter))
    }

    public observeSleepOptions(): Observable<TravelSleep> {
        return this.es.pluckData(this.es.observeTravelPage(), ['sleep']).map(sleep => {
            return {
                hotels: EventTravel.sortLists(sleep, 'hotels'),
                tents: EventTravel.sortLists(sleep, 'tents'),
                rentalHomes:  EventTravel.sortLists(sleep, 'rentalHomes')
            }
        })
    }

    public observeTransportation() : Observable<TravelTransportation> {
        return this.es.pluckData(this.es.observeTravelPage(), ['transportation']).map(transportation => {
            return {
                flights: EventTravel.sortLists(transportation, 'flights'),
                rentalCars: EventTravel.sortLists(transportation, 'rentalCars'),
                taxis: EventTravel.sortLists(transportation, 'taxis'),
                trains: EventTravel.sortLists(transportation, 'trains'),
                ferries: EventTravel.sortLists(transportation, 'ferries'),
                shuttles: EventTravel.sortLists(transportation, 'shuttles').map(shuttle => { shuttle.items = EventTravel.sortLists(shuttle, 'items'); return shuttle; })
            }
        })
    }

    public observeNotes(): Observable<Array<TravelNote>> {
        return this.es.pluckData(this.es.observeTravelPage(), ['notes'])
        .map(notes => notes || {})
        .map(notes => Object.keys(notes)
            .map((key, idx) => {
                notes[key].key = key;
                notes[key].sortKey = (notes[key].sortKey && !isNaN(parseInt(notes[key].sortKey))) ? parseInt(notes[key].sortKey) : idx;
                notes[key].type = 'notes';
                return notes[key] 
            })
            .filter(x => x))
            .map(data => data.sort(nullSorter)
        )
    }

    public observeBookings(): Observable<Array<TravelBooking>> {
        return this.es.pluckData(this.es.observeTravelPage(), ['bookings'])
        .map(bookings => bookings || {})
        .map(bookings => Object.keys(bookings)
            .map((key, idx) => {
                bookings[key].key = key;
                bookings[key].sortKey = (bookings[key].sortKey && !isNaN(parseInt(bookings[key].sortKey))) ? parseInt(bookings[key].sortKey) : idx;
                bookings[key].type = 'bookings';
                return bookings[key] 
            })
            .filter(x => x))
            .map(data => data.sort(nullSorter)
        )
    }

    public observePrimaryFaqPhoto(): Observable<Photo> {
        return this.es.observePrimaryPhoto(this.es.observeTravelPage());
    }
    
    public observePrimaryFaqPhotoHorizontal(): Observable<Photo> {
        return this.es.observePrimaryPhotoHorizontal(this.es.observeTravelPage());
    }

    public observeCombinedTravel(): Observable<any> {
        return Observable.combineLatest(
            this.observeTransportation().pluck('flights'),
            this.observeTransportation().pluck('rentalCars'),
            this.observeTransportation().pluck('taxis'),
            this.observeTransportation().pluck('trains'),
            this.observeTransportation().pluck('ferries'),
            this.observeTransportation().pluck('shuttles'),
            this.observeSleepOptions().pluck('hotels'),
            this.observeSleepOptions().pluck('tents'),
            this.observeSleepOptions().pluck('rentalHomes'),
            this.observeNotes(),
            this.observeLists(),
            this.observeBookings(),
        ).map((x: Array<any>) => {
            return x.reduce((acc: TravelItem[], curr: TravelItem[]) => {
                return [...acc, ...curr]
            }, []).sort(nullSorter);
        })
    }
}
